import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Contact, Plus, Pencil, Phone, Mail, MapPin, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { ImportExportButtons } from "@/components/ImportExportButtons";
import { MasterRejectDialog } from "@/components/MasterRejectDialog";
import { DT } from "@/components/ui/denseTable";
import { ListPagination, usePager } from "@/components/ui/ListPagination";
import { ChangeDiff, CUSTOMER_LABELS } from "@/components/ChangeDiff";
import { formatINR } from "@/lib/money";
import { SHOW_COSTING } from "@/lib/features";
import { useT } from "@/lib/i18n";
import { TranslatedName } from "@/components/TranslatedName";
import { loc } from "@/lib/numerals";

const EMPTY = { name: "", gst_tax_id: "", contact_person: "", phone: "", email: "", address: "", categories: "", payment_terms: "", credit_limit: "", notes: "" };

export function CustomerDialog({ customer, resubmit = false, needsReview = false, onClose, onSave }) {
  const init = customer
    ? { ...customer, categories: (customer.categories || []).join(", "), credit_limit: customer.credit_limit || "" }
    : EMPTY;
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) return;
    const payload = {
      name: f.name.trim(), gst_tax_id: f.gst_tax_id, contact_person: f.contact_person, phone: f.phone, email: f.email,
      address: f.address, payment_terms: f.payment_terms, notes: f.notes,
      categories: (f.categories || "").split(",").map((x) => x.trim()).filter(Boolean),
      credit_limit: f.credit_limit === "" ? 0 : Number(f.credit_limit),
    };
    await onSave(customer, payload);
    onClose();
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="customer-master-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{customer ? (resubmit ? `Edit & Resubmit ${customer.name}` : `Edit ${customer.name}`) : "New Customer"}</DialogTitle>
          <DialogDescription className="text-xs">
            {resubmit
              ? "Update the record and send it back for admin approval."
              : needsReview
                ? "Customer master record for selling finished goods. New records — and edits to Active records — go to Pending Approval until an admin approves the changes."
                : "Customer master record for selling finished goods."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="space-y-1"><Label className="text-xs">Name * (saved UPPERCASE)</Label><Input value={f.name} onChange={set("name")} data-testid="customer-name-input" /></div>
          <div className="space-y-1"><Label className="text-xs">GST / Tax ID</Label><Input value={f.gst_tax_id} onChange={set("gst_tax_id")} data-testid="customer-gst-input" /></div>
          {customer
            ? <div className="space-y-1"><Label className="text-xs">Code</Label><Input value={f.code || ""} readOnly className="bg-slate-50" data-testid="customer-code-input" /></div>
            : <div className="space-y-1"><Label className="text-xs">Code</Label><Input value="Auto (CUST-####)" readOnly className="bg-slate-50 text-slate-400" data-testid="customer-code-auto" /></div>}
          <div className="space-y-1"><Label className="text-xs">Contact Person</Label><Input value={f.contact_person} onChange={set("contact_person")} data-testid="customer-contact-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="customer-phone-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={f.email} onChange={set("email")} data-testid="customer-email-input" /></div>
          {SHOW_COSTING && <div className="space-y-1"><Label className="text-xs">Credit Limit (₹)</Label><Input type="number" min="0" value={f.credit_limit} onChange={set("credit_limit")} data-testid="customer-credit-input" /></div>}
          <div className="space-y-1 col-span-2"><Label className="text-xs">Address</Label><Input value={f.address} onChange={set("address")} data-testid="customer-address-input" /></div>
          {SHOW_COSTING && <div className="space-y-1 col-span-2"><Label className="text-xs">Payment Terms</Label><Input value={f.payment_terms} onChange={set("payment_terms")} placeholder="Net 30" data-testid="customer-terms-input" /></div>}
          <div className="space-y-1 col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="customer-notes-input" /></div>
        </div>
        <DialogFooter className="mt-4">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className={`h-8 text-xs ${resubmit ? "bg-purple-600 hover:bg-purple-700" : "bg-blue-600 hover:bg-blue-700"}`} onClick={save} data-testid="customer-master-save">
            {resubmit ? "Resubmit for Approval" : customer ? "Save Changes" : "Create Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE = {
  "Pending Approval": "bg-purple-50 text-purple-700",
  Rejected: "bg-rose-50 text-rose-700",
};

export default function CustomersPanel({ customers, isAdmin, canManage = false, currentEmail, onCreate, onUpdate, onApprove, onReject, onResubmit, onImported }) {
  const [dialog, setDialog] = useState(null); // {new:true} | {customer} | {customer, resubmit:true}
  const [rejecting, setRejecting] = useState(null);
  const list = customers || [];
  const { pageItems, pagerProps } = usePager(list);
  const t = useT();
  const statusOf = (c) => c.status || (c.active !== false ? "Active" : "Inactive");
  const canReview = (c) => canManage && (!c.created_by || c.created_by === currentEmail); // planners manage their own submissions
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="customers-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Contact className="h-4 w-4 text-blue-600" />
        <h3 className="font-display font-semibold text-slate-800">{t("customers.title")}</h3>
        <span className="text-xs text-slate-400" data-testid="customer-count">{loc(String(list.filter((c) => c.active !== false).length))} {t("common.active")} · {loc(String(list.length))} {t("common.total")}</span>
        {(isAdmin || canManage) && (
          <div className="ml-auto flex items-center gap-1.5">
            {isAdmin && <ImportExportButtons entity="customers" label="customers" onImported={onImported} />}
            <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => setDialog({ new: true })} data-testid="add-customer-btn">
              <Plus className="h-3 w-3 mr-1" /> {t("customers.add")}
            </Button>
          </div>
        )}
      </div>
      <div className={DT.wrap}>
        <table className={`${DT.table} min-w-[820px]`}>
          <thead className={DT.thead}>
            <tr className={DT.headerRow}>
              <th className="text-left font-semibold px-3 py-2">{t("col.customer")}</th>
              <th className="text-left font-semibold px-3 py-2">{t("common.contact")}</th>
              {SHOW_COSTING && <th className="text-left font-semibold px-3 py-2">{t("col.terms")}</th>}
              {SHOW_COSTING && <th className="text-right font-semibold px-3 py-2">{t("col.credit")}</th>}
              <th className="text-center font-semibold px-3 py-2">{t("common.status")}</th>
              <th className="text-right font-semibold px-3 py-2">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 && <tr><td colSpan={SHOW_COSTING ? 6 : 4} className="px-3 py-8 text-center text-slate-400" data-testid="customers-empty">{t("customers.empty")}</td></tr>}
            {pageItems.map((c) => {
              const st = statusOf(c);
              return (
              <tr key={c.id} className={`border-b border-slate-100 transition-colors duration-150 ${st === "Rejected" ? "bg-rose-50/70 hover:bg-rose-100/80" : st === "Pending Approval" ? "bg-yellow-50/70 hover:bg-yellow-100/80" : `even:bg-slate-50/60 hover:bg-slate-100/80 ${c.active === false ? "opacity-50" : ""}`}`} data-testid={`customer-row-${c.id}`}>
                <td className="px-3 py-2">
                  <p className="font-semibold text-slate-800"><TranslatedName text={c.name} /></p>
                  <p className="text-[10px] text-slate-400 font-mono-data">{c.code}{c.gst_tax_id ? ` · ${c.gst_tax_id}` : ""}</p>
                  {st === "Rejected" && c.rejection_reason && (
                    <p className="text-[10px] text-rose-600 italic mt-0.5" data-testid={`customer-rejection-${c.id}`}>Rejected: {c.rejection_reason}</p>
                  )}
                  {st === "Pending Approval" && (
                    <p className="text-[10px] text-purple-500 mt-0.5" data-testid={`customer-pending-${c.id}`}>Awaiting admin approval{c.created_by ? ` · raised by ${c.created_by}` : ""}</p>
                  )}
                  {(st === "Pending Approval" || st === "Rejected") && (
                    <ChangeDiff changes={c.pending_changes} labels={CUSTOMER_LABELS} testidPrefix={`customer-${c.id}`} />
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {c.contact_person && <p className="font-medium">{c.contact_person}</p>}
                  <p className="flex items-center gap-1 text-[11px]"><Phone className="h-3 w-3" />{c.phone || "—"}</p>
                  <p className="flex items-center gap-1 text-[11px] truncate max-w-[180px]"><Mail className="h-3 w-3" />{c.email || "—"}</p>
                  {c.address && <p className="flex items-center gap-1 text-[10px] text-slate-400 truncate max-w-[180px]"><MapPin className="h-3 w-3" />{c.address}</p>}
                </td>
                {SHOW_COSTING && <td className="px-3 py-2 text-slate-600">{c.payment_terms || "—"}</td>}
                {SHOW_COSTING && <td className="px-3 py-2 text-right font-mono-data text-slate-600" data-testid={`customer-credit-${c.id}`}>{c.credit_limit ? formatINR(c.credit_limit, { decimals: 0 }) : "—"}</td>}
                <td className="px-3 py-2 text-center">
                  {STATUS_BADGE[st] ? (
                    <span className={`inline-block px-2 py-0.5 rounded font-mono-data text-[10px] ${STATUS_BADGE[st]}`} data-testid={`customer-status-${c.id}`}>{st}</span>
                  ) : isAdmin
                    ? <Switch checked={c.active !== false} onCheckedChange={(v) => onUpdate(c, { active: v })} data-testid={`customer-active-${c.id}`} />
                    : <span className={`text-[10px] ${c.active !== false ? "text-emerald-600" : "text-slate-400"}`}>{c.active !== false ? t("common.active") : t("common.inactive")}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {isAdmin && st === "Pending Approval" && (
                      <>
                        <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => onApprove(c)} data-testid={`approve-customer-${c.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> {t("common.approve")}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-600 border-rose-200" onClick={() => setRejecting(c)} data-testid={`reject-customer-${c.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> {t("common.reject")}
                        </Button>
                      </>
                    )}
                    {canManage && !isAdmin && st === "Rejected" && canReview(c) && (
                      <Button size="sm" className="h-7 text-[11px] bg-purple-600 hover:bg-purple-700" onClick={() => setDialog({ customer: c, resubmit: true })} data-testid={`resubmit-customer-${c.id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> {t("common.resubmit")}
                      </Button>
                    )}
                    {(isAdmin || (canManage && st !== "Rejected" && (st === "Active" || canReview(c)))) && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setDialog({ customer: c })} data-testid={`edit-customer-${c.id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> {t("common.edit")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <ListPagination {...pagerProps} testid="customers-pagination" />
      </div>
      {dialog && (
        <CustomerDialog
          customer={dialog.customer || null}
          resubmit={!!dialog.resubmit}
          needsReview={!isAdmin && canManage}
          onClose={() => setDialog(null)}
          onSave={(existing, payload) => (existing ? (dialog.resubmit ? onResubmit(existing, payload) : onUpdate(existing, payload)) : onCreate(payload))}
        />
      )}
      {rejecting && (
        <MasterRejectDialog
          title={`Reject ${rejecting.name}`}
          onClose={() => setRejecting(null)}
          onConfirm={(reason) => onReject(rejecting, reason)}
        />
      )}
    </div>
  );
}
